import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { HelpCircle, Sparkles, Trash2, Loader2, Filter, Search } from 'lucide-react';

export default function Questions() {
  const queryClient = useQueryClient();
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [questionsPerCategory, setQuestionsPerCategory] = useState(5);

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: api.getCategories
  });

  const { data: questions, isLoading } = useQuery({
    queryKey: ['questions', selectedLocation, selectedCategory, selectedStatus],
    queryFn: () => api.getQuestions({
      location_id: selectedLocation || undefined,
      category_id: selectedCategory || undefined,
      status: selectedStatus || undefined
    })
  });

  const generateMutation = useMutation({
    mutationFn: ({ locationId, count }: { locationId: string; count: number }) =>
      api.generateQuestions(locationId, count),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
    }
  });

  const statusColors: Record<string, string> = {
    pending: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800',
    generating: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800',
    generated: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800',
    published: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800',
    archived: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'
  };

  const intentColors: Record<string, string> = {
    informational: 'text-blue-600 bg-blue-50',
    transactional: 'text-green-600 bg-green-50',
    local: 'text-purple-600 bg-purple-50',
    comparison: 'text-orange-600 bg-orange-50'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Questions</h1>
          <p className="text-gray-500">Generate and manage Q&A content questions</p>
        </div>
      </div>

      {/* Generate Questions Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Questions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">Select a location</option>
              {locations?.map((loc: any) => (
                <option key={loc.id} value={loc.id}>
                  {loc.city}, {loc.state}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Questions per Category</label>
            <input
              type="number"
              value={questionsPerCategory}
              onChange={(e) => setQuestionsPerCategory(parseInt(e.target.value) || 5)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              min={1}
              max={20}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                if (selectedLocation) {
                  generateMutation.mutate({
                    locationId: selectedLocation,
                    count: questionsPerCategory
                  });
                }
              }}
              disabled={!selectedLocation || generateMutation.isPending}
              className="btn-primary w-full"
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Questions
                </>
              )}
            </button>
          </div>
        </div>
        {generateMutation.isSuccess && (
          <p className="mt-4 text-sm text-green-600">
            Successfully generated {generateMutation.data?.questions_generated} questions!
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <span className="font-medium text-gray-900">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">All Locations</option>
              {locations?.map((loc: any) => (
                <option key={loc.id} value={loc.id}>
                  {loc.city}, {loc.state}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">All Categories</option>
              {categories?.map((cat: any) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="generated">Generated</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>
      </div>

      {/* Questions List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="space-y-4">
          {Array.isArray(questions) && questions.length > 0 ? (
            questions.map((question: any) => (
              <div key={question.id} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                    <HelpCircle className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 mb-1">
                      {question.question_text}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className={statusColors[question.status] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'}>
                        {question.status}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${intentColors[question.search_intent] || ''}`}>
                        {question.search_intent}
                      </span>
                      <span className="text-gray-500">
                        Priority: {question.priority_score}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteMutation.mutate(question.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="card text-center py-12">
              <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No questions yet</h3>
              <p className="text-gray-500">Select a location and generate questions to get started</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
